import type { ReactNode } from 'react';
import { toast as manager } from '@/components/ui/toast';

type Options = {
  description?: ReactNode;
  /** A button on the toast, e.g. Undo; the toast closes when it is clicked. */
  action?: { label: ReactNode; onClick: () => void };
  /** Milliseconds before it closes; 0 keeps it open. */
  duration?: number;
};

type ToastType = 'success' | 'error' | 'info' | 'warning';

function show(type: ToastType, title: ReactNode, options: Options = {}) {
  return manager.add({
    title,
    type,
    description: options.description,
    timeout: options.duration,
    // Errors interrupt screen readers; the rest wait their turn.
    priority: type === 'error' ? 'high' : 'low',
    actionProps: options.action ? { children: options.action.label, onClick: options.action.onClick } : undefined,
  });
}

/**
 * The app's toasts (shadcn Toast on Base UI), with the sonner-style calls the
 * pages already make: `toast.success('Saved')`, `toast.error(message)`,
 * `toast.success('Moved', { action: { label: 'Undo', onClick } })`.
 */
export const toast = {
  success: (title: ReactNode, options?: Options) => show('success', title, options),
  error: (title: ReactNode, options?: Options) => show('error', title, options),
  info: (title: ReactNode, options?: Options) => show('info', title, options),
  warning: (title: ReactNode, options?: Options) => show('warning', title, options),
  dismiss: (id?: string) => (id ? manager.close(id) : undefined),
};
